import React, { useState, useEffect } from 'react';
import { useAuth } from '../../helpers/AuthContext';
import Section from '../Section';

function ForumPost({ post, latestComment }) {
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString();
    };

    return (
        <div className="forum-post" style={{ marginBottom: '1rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '4px' }}>
            <h4 style={{ margin: '0 0 0.5rem 0' }}>
                <a href={`/account/help/forums/thread/${post.uuid}`} className="text-link" style={{ textDecoration: 'none' }}>
                    {post.title || 'Untitled Post'}
                </a>
            </h4>
            <div className="text-muted" style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                Posted by {post.Shopper?.displayName || 'Anonymous'} on {formatDate(post.createdAt)}
            </div>
            <div style={{ fontSize: '0.95rem', marginBottom: '0.75rem' }}>
                {post.comment ? post.comment.substring(0, 200) + (post.comment.length > 200 ? '...' : '') : 'No content'}
            </div>
            {latestComment && (
                <div className="well" style={{ 
                    padding: '0.5rem', 
                    borderRadius: '3px',
                    fontSize: '0.85rem',
                    borderLeft: '3px solid #0066cc'
                }}>
                    <strong>Latest comment:</strong> {latestComment.Shopper?.displayName || 'Anonymous'} - {formatDate(latestComment.createdAt)}
                    <br />
                    {latestComment.comment ? latestComment.comment.substring(0, 100) + (latestComment.comment.length > 100 ? '...' : '') : ''}
                </div>
            )}
        </div>
    );
}

function ForumSection({ forum, posts }) {
    return (
        <div className="forum-section" style={{ marginBottom: '2rem' }}>
            <h3 style={{ margin: '0 0 1rem 0' }}>
                <a href={`/account/help/forums/${forum.slug}`} className="text-link" style={{ textDecoration: 'none' }}>
                    {forum.name}
                </a>
            </h3>
            <p className="text-muted" style={{ margin: '0 0 1rem 0', fontSize: '0.9rem' }}>
                {forum.description}
            </p>
            <div className="text-subtle" style={{ fontSize: '0.85rem', marginBottom: '1rem' }}>
                {forum.commentCount || 0} posts+comments | 
                Last activity: {forum.latestCommentAt ? new Date(forum.latestCommentAt).toLocaleString() : 'No activity yet'}
            </div>
            {posts && posts.length > 0 ? (
                <div>
                    {posts.map(post => (
                        <ForumPost 
                            key={post.uuid} 
                            post={post} 
                            latestComment={post.latestComment}
                        />
                    ))}
                </div>
            ) : (
                <p className="text-subtle" style={{ fontStyle: 'italic' }}>No posts yet in this forum.</p>
            )}
        </div>
    );
}

export default function SellerForum() {
    const [forums, setForums] = useState([]);
    const [forumPosts, setForumPosts] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const auth = useAuth();

    useEffect(() => {
        const fetchForumData = async () => {
            try {
                setLoading(true);
                
                // Fetch forums
                const forumsResponse = await fetch(`/account/help/forums`, {
                    credentials: 'include',
                    headers: {
                        'Accept': 'application/json'
                    }
                });
                
                if (!forumsResponse.ok) {
                    throw new Error(`Failed to fetch forums: ${forumsResponse.status}`);
                }
                
                const forumsData = await forumsResponse.json();
                setForums(forumsData);

                // Fetch posts for each forum
                const postsData = {};
                for (const forum of forumsData) {
                    try {
                        const postsResponse = await fetch(`/account/help/forums/${forum.slug}`, {
                            credentials: 'include',
                            headers: {
                                'Accept': 'application/json'
                            }
                        });
                        
                        if (postsResponse.ok) {
                            const posts = await postsResponse.json();
                            postsData[forum.uuid] = posts;
                        }
                    } catch (err) {
                        console.warn(`Failed to fetch posts for forum ${forum.slug}:`, err);
                        postsData[forum.uuid] = [];
                    }
                }
                setForumPosts(postsData);
                
            } catch (err) {
                console.error('Error fetching forum data:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchForumData();
    }, [auth.API_URL]);

    if (loading) {
        return (
            <Section title="Seller Forum (React Preview)">
                <div className="text-muted" style={{ padding: '2rem', textAlign: 'center' }}>
                    Loading forum data...
                </div>
            </Section>
        );
    }

    if (error) {
        return (
            <Section title="Seller Forum (React Preview)">
                <div className="error" style={{ padding: '2rem', textAlign: 'center' }}>
                    Error loading forum data: {error}
                </div>
            </Section>
        );
    }

    return (
        <Section title="Seller Forum (React Preview)">
            <div style={{ padding: '1rem' }}>
                <p className="text-muted" style={{ marginBottom: '2rem', fontSize: '0.9rem' }}>
                    This is a React preview of the seller forum. The original EJS version is still active in the main dashboard.
                </p>
                
                {forums.length === 0 ? (
                    <p className="text-subtle" style={{ textAlign: 'center', fontStyle: 'italic' }}>
                        No forums available.
                    </p>
                ) : (
                    forums.map(forum => (
                        <ForumSection 
                            key={forum.uuid} 
                            forum={forum} 
                            posts={forumPosts[forum.uuid] || []}
                        />
                    ))
                )}
                
                <div className="well" style={{ marginTop: '2rem', padding: '1rem', borderRadius: '4px' }}>
                    <p className="text-muted" style={{ margin: 0, fontSize: '0.85rem' }}>
                        <strong>Preview Features:</strong> This React component shows forums with their latest posts and comments. 
                        Additional features like posting, commenting, and real-time updates can be added in future iterations.
                    </p>
                </div>
            </div>
        </Section>
    );
}